import { StyleSheet, Text as RNText, View } from 'react-native';
import type { TextStyle } from 'react-native';

import { IconButton } from '../primitives/IconButton';
import { Text } from '../primitives/Text';
import { useTheme } from '../theme/ThemeProvider';
import {
  EMPTY_VALUE_PLACEHOLDER,
  hasText,
  shouldShowEmptyPlaceholder,
} from './dataPanelItemBase';
import type { DataPanelItemBaseProps } from './dataPanelItemBase';

export function DataPanelItemValue({
  props,
  color,
  style,
}: {
  props: DataPanelItemBaseProps;
  color?: string;
  style?: TextStyle;
}) {
  const theme = useTheme();
  const {
    value,
    linkUrl,
    onLinkPress,
    showEmptyPlaceholder,
    showEmptyPlaceholderWhen,
    emptyValuePlaceholder = EMPTY_VALUE_PLACEHOLDER,
  } = props;

  if (shouldShowEmptyPlaceholder(value, showEmptyPlaceholder, showEmptyPlaceholderWhen)) {
    return (
      <Text color={color ?? theme.colors.textMuted} style={style}>
        {emptyValuePlaceholder}
      </Text>
    );
  }

  if (value == null) {
    return null;
  }

  if (linkUrl != null && onLinkPress != null) {
    return (
      <RNText
        accessibilityRole="link"
        onPress={() => void onLinkPress(linkUrl)}
        style={[{ color: theme.colors.primary }, styles.link, style]}
      >
        {value}
      </RNText>
    );
  }

  return (
    <Text color={color} style={style}>
      {value}
    </Text>
  );
}

export function DataPanelItemAffordances({ props }: { props: DataPanelItemBaseProps }) {
  const {
    actionItems,
    allowCopyToClipboard,
    value,
    valueToCopy,
    onCopy,
    copyAccessibilityLabel,
    editable,
    onEditClick,
    editAccessibilityLabel,
  } = props;

  const effectiveValueToCopy = valueToCopy ?? value;
  const showCopy = allowCopyToClipboard === true && onCopy != null && hasText(effectiveValueToCopy);

  if (actionItems == null && !showCopy && editable !== true) {
    return null;
  }

  return (
    <View style={styles.affordances}>
      {actionItems}

      {showCopy ? (
        <IconButton
          icon="copy"
          size="sm"
          tone="muted"
          accessibilityLabel={copyAccessibilityLabel}
          onPress={() => onCopy(effectiveValueToCopy)}
        />
      ) : null}

      {editable === true ? (
        <IconButton
          icon="pencil"
          size="sm"
          tone="muted"
          accessibilityLabel={editAccessibilityLabel}
          onPress={onEditClick}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  link: { textDecorationLine: 'underline' },
  affordances: { flexDirection: 'row', alignItems: 'center' },
});
